import { observable, configure, action, computed } from 'mobx';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class PaypalStore {
    constructor() {

    }

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    updateObjectKeyValue(obj, key, value) {
        this[obj][key] = value;
    }

    @observable redirectUrl = null;

    async requestPayment(params = {}) {
        if (!params.amount || !params.title) {
            console.error('paypalStore.requestPayment', 'Missing amount and/or title');
            return;
        }
        const response = await util.fetchApi(`/api/paypal/order-create`, { method: 'POST' }, {
            ...params,
        });
        switch (response.status) {
            case 200:
                // console.log('paypalStore.requestPayment', response);
                this.updateKeyValue('redirectUrl', response.data.redirectUrl);
                return response;
            default:
                console.error('paypalStore.requestPayment', response);
                return response;
        }
    }

    async checkPaymentStatus(uuidv4) {
        const response = await util.fetchApi(`/api/paypal/check-status/${uuidv4}`);
        switch (response.status) {
            case 200:
                // console.log('paypalStore.checkPaymentStatus', response);
                return response;
            default:
                console.error('paypalStore.checkPaymentStatus', response);
                return response;
        }
    }

    /**
     * Poll payment status until it's completed or timeout
     * @param {string} uuidv4 - The payment UUID
     * @param {Function} onUpdate - Callback on each poll iteration with current status
     * @param {Function} onComplete - Callback when payment is completed (status 3)
     * @param {Function} onFailed - Callback when payment failed/cancelled (status >= 90)
     * @param {number} maxAttempts - Maximum number of polling attempts (default: 60)
     * @param {number} interval - Polling interval in milliseconds (default: 2000)
     * @param {number} maxInterval - Upper bound for the backed-off interval (default: interval)
     * @param {Function} shouldStop - Return true to abandon polling (unmount, settled elsewhere)
     * @returns {Promise<void>}
     */
    @action
    async pollPaymentStatus(uuidv4, {
        onUpdate, onComplete, onFailed, shouldStop, maxAttempts = 60, interval = 2000, maxInterval,
    } = {}) {
        let attempts = 0;
        let currentInterval = interval;
        const ceiling = maxInterval || interval;

        const poll = async () => {
            attempts++;

            if (shouldStop && shouldStop()) {
                return;
            }

            if (attempts > maxAttempts) {
                console.log(`[PaypalStore] Max polling attempts reached for ${uuidv4}`);
                if (onFailed) {
                    onFailed({ timeout: true, attempts });
                }
                return;
            }

            const response = await this.checkPaymentStatus(uuidv4);

            // Call onUpdate callback with current status (for UI updates)
            if (onUpdate && response?.data) {
                onUpdate(response.data);
            }

            if (response?.data?.paymentStatus === 3) {
                // Payment completed
                console.log(`[PaypalStore] Payment ${uuidv4} completed after ${attempts} attempts`);
                if (onComplete) {
                    onComplete(response.data);
                }
                return;
            }

            if (response?.data?.paymentStatus >= 90) {
                // Payment failed/cancelled
                console.log(`[PaypalStore] Payment ${uuidv4} failed/cancelled (status: ${response.data.paymentStatus})`);
                if (onFailed) {
                    onFailed(response.data);
                }
                return;
            }

            if (response?.status === 404) {
                // Payment is gone (removed, or belongs to someone else). Stop polling so the UI
                // doesn't hang on "checking" until the max-attempts timeout.
                console.log(`[PaypalStore] Payment ${uuidv4} not found (404) — treating as cancelled`);
                if (onFailed) {
                    onFailed({ cancelled: true });
                }
                return;
            }

            // Every poll costs a PayPal getOrder call, so ease off while the buyer is still on
            // PayPal's site. The reservation window is 20 minutes; a tight interval for that long
            // is what made the checkout hammer the PayPal API.
            currentInterval = Math.min(Math.round(currentInterval * 1.15), ceiling);

            // Continue polling
            setTimeout(poll, currentInterval);
        };

        poll();
    }
}

const store = new PaypalStore();
export default store;
